import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  usePropertyField,
  usePropertyAddressUrl,
  useCollectionBinding,
  useTokens,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import type { Floorplan } from '@vibesites/schema';
import pageOverrides from '../pages.json';
import { isFloorplanPageOffered } from '../utils/floorplanTypes';
import { bakedPages } from '../utils/bakedPages';
import basePages from './sitemap-pages.json';
import themePages from './theme-pages.json';
import { REMOVED_PAGE_ALIASES } from '../utils/removedPages';
import { VibesitesImage, parseStorageKey } from '@vibesites/ui-primitives';
import { contrastRatio, getContrastTextColor } from '../utils/luminance';
import { useHasApartmentSyncIds } from '../hooks/useHasApartmentSyncIds';
import { useLogoVisibleOnBackground } from '../hooks/useLogoVisibleOnBackground';

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
  sections?: { slot?: string; props?: Record<string, unknown> }[];
  deleted_at?: string | null;
}

/**
 * Company/child themes only ship section (and other) overrides in
 * `pages.json` — name / path / position / parent live on the parent theme.
 * SitemapContent already mirrors that with `sitemap-pages.json`; Header must
 * do the same shallow merge or every nav label/href is undefined and the
 * desktop links render blank.
 */
function mergePageEntries(
  base: Record<string, Partial<PageEntry>>,
  overrides: Record<string, Partial<PageEntry>>,
): Record<string, PageEntry> {
  const merged: Record<string, PageEntry> = {};
  for (const [alias, data] of Object.entries(base)) {
    merged[alias] = {
      ...data,
      name: data.name ?? alias,
      path: data.path ?? `/${alias}`,
      position: data.position ?? 0,
    };
  }
  for (const [alias, data] of Object.entries(overrides)) {
    const prev = merged[alias];
    merged[alias] = {
      ...prev,
      ...data,
      name: data.name ?? prev?.name ?? alias,
      path: data.path ?? prev?.path ?? `/${alias}`,
      position: data.position ?? prev?.position ?? 0,
    };
  }
  return merged;
}

// `theme-pages.json` is the parent theme's `pages.json`; this site's own
// `pages.json` only carries its custom pages, so the parent layer keeps
// inherited entries (e.g. Sister Properties) when baked pages are absent.
const pagesData = mergePageEntries(
  mergePageEntries(
    mergePageEntries(
      basePages as Record<string, Partial<PageEntry>>,
      themePages as Record<string, Partial<PageEntry>>,
    ),
    pageOverrides as Record<string, Partial<PageEntry>>,
  ),
  (bakedPages ?? {}) as Record<string, Partial<PageEntry>>,
);

/** Sister Properties page is eligible when visible and still hosts the
    SisterProperties section. */
function isSisterPropertiesPageActive(): boolean {
  const page = pagesData['sister-properties'];
  if (!page || page.visible === false) return false;
  return (page.sections ?? []).some((section) => section.slot === 'SisterProperties');
}

/** Page aliases that should never appear in the main navigation */
const NAV_EXCLUDED_ALIASES = new Set([
  'privacy-policy',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'schedule-a-tour',
  'hyperlocal',
  ...REMOVED_PAGE_ALIASES,
]);

type NavChildDef =
  | {
      alias: string;
      /** Overrides the page name inside its group (e.g. Gallery → "Photos"). */
      label?: string;
    }
  /** Off-site link driven by the `header.marketplace_link` slot group. */
  | { marketplace: true };

type NavItemDef = { alias: string } | { label: string; children: NavChildDef[] };

/** Label of the catch-all group. Eligible pages not listed in
    `NAV_STRUCTURE` are appended here so new pages never vanish. */
const CATCH_ALL_GROUP_LABEL = 'About';

/**
 * Top-level navigation layout, modeled on the Price Brothers RentCafe sites
 * (Home · Floor Plans ▾ · Gallery ▾ · Amenities ▾ · Explore ▾ · Contact Us ·
 * About ▾). Groups
 * are labels only — they toggle a dropdown rather than navigating. A group
 * with a single eligible child collapses into a plain link; a group with
 * none is dropped.
 */
const NAV_STRUCTURE: NavItemDef[] = [
  { alias: 'home' },
  {
    label: 'Floor Plans',
    children: [
      { alias: 'floor-plans', label: 'All Floor Plans' },
    ],
  },
  { label: 'Gallery', children: [{ alias: 'gallery', label: 'Photos' }, { alias: 'virtual-tour' }] },
  {
    label: 'Amenities & Pets',
    children: [
      { alias: 'amenities' },
      { alias: 'pet-policy' },
      { alias: 'c26-hub-gym-and-pool' },
      { alias: 'hawkflock' },
    ],
  },
  {
    label: 'Explore',
    children: [
      { alias: 'neighborhood' },
      { alias: 'interactive-map' },
      { alias: 'sister-properties' },
      { marketplace: true },
    ],
  },
  { alias: 'contact' },
  { label: CATCH_ALL_GROUP_LABEL, children: [{ alias: 'faq' }, { alias: 'reviews' }, { alias: 'preferred-employer-program' }] },
];

interface NavLink {
  label: string;
  /** Omitted for label-only groups, which open their dropdown instead. */
  href?: string;
  target?: string;
  /** Wraps the anchor in the `header.marketplace_link` editable slot group. */
  marketplace?: boolean;
  /** Sub-nav items rendered as a dropdown on desktop and grouped rows on
      mobile. */
  children?: NavLink[];
}

interface MarketplaceLink {
  url?: string | null;
  label?: string | null;
  _target?: string | null;
}

function NavAnchor({
  link,
  className,
  onClick,
}: {
  link: NavLink;
  className: string;
  onClick?: () => void;
}) {
  const target = link.target || '_self';
  const anchor = (
    <a
      href={link.href}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      onClick={onClick}
      className={className}
    >
      {link.label}
    </a>
  );
  if (!link.marketplace) return anchor;
  return (
    <EditableSlotGroup groupPath="header.marketplace_link" definition={marketplaceLinkDef}>
      {anchor}
    </EditableSlotGroup>
  );
}

function navKey(link: NavLink): string {
  return link.href ?? `group:${link.label}`;
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

interface FaqItem {
  question?: string | null;
  answer?: string | null;
}

/**
 * Build navigation links from pages.json, arranged by `NAV_STRUCTURE`.
 * Only includes pages that are visible and not in the exclusion list.
 * Eligible pages not named in `NAV_STRUCTURE` are appended (by position)
 * to the About group so a page is never silently dropped from the nav.
 *
 * `hasInteractiveLeasing` controls the dynamic visibility of the
 * Interactive Map page — that page renders the ApartmentSync (Repli360 /
 * RRAC) "interactive leasing view" widget, which is keyed by two per-
 * property custom fields: `custom_apartmentsync_javascript_id` (the
 * encoded token in the script URL) and `custom_apartmentsync_config_id`
 * (the site identifier). Both are declared as required-style tokens on
 * the apartment-sync-map block, and a property without both is a
 * half-configured / no-real-map state (Centre Point being the canonical
 * example — it has neither, but earlier versions of this check let it
 * leak into the nav when only one ID was populated as a placeholder).
 * If either is empty we drop the page from the nav (the SSG also marks
 * the page hidden + noindex so it isn't crawled — see ssg-entry.ts).
 *
 * Sister Properties is gated on the page being active (visible + section
 * present), not on filled community cards — editors expect the Explore link
 * as soon as the page is on. SitemapContent mirrors the same check.
 */
function buildNavLinks(
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
  hasFaqs: boolean,
  sisterPropertiesActive: boolean,
  floorplans: Floorplan[] | null | undefined,
  marketplaceLink: MarketplaceLink,
): NavLink[] {
  const eligible = Object.entries(pagesData)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.deleted_at) return false;
      if (page.template) return false;
      if (!page.name || !page.path) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      // FAQ page is only exposed when the property has published FAQs —
      // otherwise the page is a bare heading with no answers, so hide it
      // from the nav (SitemapContent mirrors this so the sitemap agrees).
      if (alias === 'faq' && !hasFaqs) return false;
      if (alias === 'sister-properties' && !sisterPropertiesActive) return false;
      if (!isFloorplanPageOffered(page, floorplans)) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const pagesByAlias = new Map(eligible);
  const placed = new Set<string>();
  const take = (alias: string, label?: string): (NavLink & { pageName: string }) | null => {
    const page = pagesByAlias.get(alias);
    if (!page || placed.has(alias)) return null;
    placed.add(alias);
    return { label: label || page.name, href: page.path, pageName: page.name };
  };
  const takeMarketplace = (): (NavLink & { pageName: string }) | null => {
    const href = (marketplaceLink.url || '').trim();
    if (!href) return null;
    const label = marketplaceLink.label || 'Bluhawk Marketplace';
    return { label, href, target: marketplaceLink._target || '_blank', marketplace: true, pageName: label };
  };

  const groups: { label: string; children: (NavLink & { pageName: string })[] }[] = [];
  const items: (NavLink | (typeof groups)[number])[] = [];
  for (const def of NAV_STRUCTURE) {
    if ('alias' in def) {
      const link = take(def.alias);
      if (link) items.push(link);
      continue;
    }
    const group = {
      label: def.label,
      children: def.children
        .map((child) => ('marketplace' in child ? takeMarketplace() : take(child.alias, child.label)))
        .filter((l): l is NavLink & { pageName: string } => !!l),
    };
    groups.push(group);
    items.push(group);
  }

  let catchAllGroup = groups.find((g) => g.label === CATCH_ALL_GROUP_LABEL);
  if (!catchAllGroup) {
    catchAllGroup = { label: CATCH_ALL_GROUP_LABEL, children: [] };
    items.push(catchAllGroup);
  }
  for (const [alias] of eligible) {
    const link = take(alias);
    if (link) catchAllGroup.children.push(link);
  }

  const strip = ({ label, href, target, marketplace }: NavLink): NavLink => ({ label, href, target, marketplace });
  return items.flatMap((item): NavLink[] => {
    if (!item.children) return [strip(item)];
    if (item.children.length === 0) return [];
    if (item.children.length === 1) {
      const only = item.children[0] as NavLink & { pageName: string };
      return [{ ...strip(only), label: only.pageName }];
    }
    return [{ label: item.label, children: item.children.map(strip) }];
  });
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/apply-now',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Off-site link listed under the Explore dropdown. Clearing the URL drops it
// from the nav.
const marketplaceLinkDef: SlotGroupDefinition = {
  label: 'Explore: Marketplace Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.bluhawk.com/' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Bluhawk Marketplace' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/schedule-a-tour',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Self-guided tour CTA — only rendered when `custom_self_guided_tour_url`
// (or an editor override) has a value. No defaultValue on the URL so an
// empty/missing field hides the button entirely rather than falling back
// to the main schedule-a-tour page.
const selfGuidedTourLinkDef: SlotGroupDefinition = {
  label: 'Self-Guided Tour Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'custom_self_guided_tour_url',
      suggestedFields: ['custom_self_guided_tour_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Self-Guided Tours' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// Address row inside the mobile drawer. Promoted from a bare
// `header.full_address` content token + auto-generated Google Maps
// directions URL to a slot group so editors can pick the address text,
// the destination URL, and the link target independently — rather than
// having the URL forced to the property's `google_place_url` (or a
// synthesized `/maps/dir/?destination=...` fallback) with no override.
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL built from place / business / full
    // address / lat-lon fallbacks). An explicit editor override wins.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// ============================================================================
// Chevron Down Icon for "More" dropdown
// ============================================================================

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

// ============================================================================
// Desktop Nav Link
// ============================================================================

function DesktopNavLink({ link }: { link: NavLink }) {
  const hasChildren = !!(link.children && link.children.length);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Click-outside / Escape close — mirrors the "More" dropdown so the menu
  // dismisses cleanly when focus or pointer leaves the parent group.
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  if (!hasChildren) {
    return (
      <NavAnchor
        link={link}
        className="rise-nav-darken-hover font-body text-[13px] font-semibold tracking-wide 2xl:tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 whitespace-nowrap"
      />
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="nav-dropdown-group relative"
      onPointerEnter={(e) => e.pointerType === 'mouse' && setOpen(true)}
      onPointerLeave={(e) => e.pointerType === 'mouse' && setOpen(false)}
    >
      {link.href ? (
        <a
          href={link.href}
          className="rise-nav-darken-hover font-body text-[13px] font-semibold tracking-wide 2xl:tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap"
          aria-haspopup="true"
          aria-expanded={open}
          onFocus={() => setOpen(true)}
        >
          {link.label}
          <ChevronDownIcon
            className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </a>
      ) : (
        <button
          type="button"
          className="rise-nav-darken-hover font-body text-[13px] font-semibold tracking-wide 2xl:tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          onFocus={() => setOpen(true)}
        >
          {link.label}
          <ChevronDownIcon
            className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </button>
      )}

      <div
        className={`nav-more-dropdown absolute left-0 top-full border border-[var(--vibe-color-border)] shadow-lg min-w-[240px] z-[70] transition-all duration-200 ${
          open
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-1'
        }`}
        style={{ backgroundColor: 'var(--vibe-color-background)' }}
      >
        {link.children!.map((child) => (
          <NavAnchor
            key={child.href}
            link={child}
            className="rise-nav-darken-hover block px-5 py-3 font-body text-[13px] font-semibold tracking-wide uppercase text-[var(--vibe-color-text)] transition-colors whitespace-nowrap"
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Desktop Navigation with dynamic "More" overflow dropdown
// ============================================================================

function DesktopNav({ navLinks }: { navLinks: NavLink[] }) {
  const navContainerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(navLinks.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  const recalculate = useCallback(() => {
    const container = navContainerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const availableWidth = container.offsetWidth;
    const children = Array.from(measure.children) as HTMLElement[];
    const computedGap = parseFloat(getComputedStyle(container).gap) || 20;
    const moreButtonWidth = 90;

    let totalWidth = 0;
    let fitCount = 0;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      const widthWithGap = i === 0 ? childWidth : childWidth + computedGap;
      const reserveMore = i < children.length - 1 ? moreButtonWidth + computedGap : 0;
      const needed = totalWidth + widthWithGap + reserveMore;

      if (needed > availableWidth && i < children.length) {
        // Keep the remaining links in the row if they collectively still
        // fit without an overflow chip.
        let testWidth = totalWidth + widthWithGap;
        let allFit = true;
        for (let j = i + 1; j < children.length; j++) {
          testWidth += children[j].offsetWidth + computedGap;
          if (testWidth > availableWidth) {
            allFit = false;
            break;
          }
        }
        fitCount = allFit ? children.length : i;
        break;
      }
      totalWidth += widthWithGap;
      fitCount = i + 1;
    }

    setVisibleCount(fitCount);
  }, [navLinks.length]);

  useEffect(() => {
    recalculate();
    const observer = new ResizeObserver(() => recalculate());
    if (navContainerRef.current) {
      observer.observe(navContainerRef.current);
    }
    return () => observer.disconnect();
  }, [recalculate]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    }
    if (moreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [moreOpen]);

  const visibleLinks = navLinks.slice(0, visibleCount);
  const overflowLinks = navLinks.slice(visibleCount);

  return (
    <>
      {/* Hidden measurement row. Parent links with children render with a
          chevron in the live nav, so we mirror that here so the per-link
          offsetWidth used by the overflow calculation matches the visible
          width — otherwise the chevron would push the rightmost link out
          unexpectedly when the row is near full. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="flex items-center gap-5 absolute pointer-events-none"
        style={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0, whiteSpace: 'nowrap' }}
      >
        {navLinks.map((link) => (
          <span
            key={navKey(link)}
            className="font-body text-[13px] font-semibold tracking-wide 2xl:tracking-wider uppercase inline-flex items-center gap-1"
          >
            {link.label}
            {link.children && link.children.length ? (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            ) : null}
          </span>
        ))}
      </div>

      {/* Visible navigation */}
      <nav
        ref={navContainerRef}
        className="hidden lg:flex items-center justify-end gap-4 xl:gap-5 2xl:gap-7 flex-1 min-w-0 mx-4 xl:mx-6"
        role="navigation"
        aria-label="Main navigation"
      >
        {visibleLinks.map((link) => (
          <DesktopNavLink key={navKey(link)} link={link} />
        ))}

        {overflowLinks.length > 0 && (
          <div
            ref={moreRef}
            className="nav-dropdown-group relative"
            // Mirror the DesktopNavLink hover behavior — the "More"
            // overflow dropdown opens on hover/focus just like the
            // group dropdowns so the two affordances feel
            // identical. The click handler stays as a fallback for
            // touch devices where hover doesn't fire, and the
            // click-outside listener still dismisses the menu when the
            // pointer leaves the group entirely.
            onPointerEnter={(e) => e.pointerType === 'mouse' && setMoreOpen(true)}
            onPointerLeave={(e) => e.pointerType === 'mouse' && setMoreOpen(false)}
          >
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              onFocus={() => setMoreOpen(true)}
              className="rise-nav-darken-hover font-body text-[13px] font-semibold tracking-wide 2xl:tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap"
              aria-expanded={moreOpen}
              aria-haspopup="true"
            >
              More
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
            </button>

            <div
              className={`nav-more-dropdown absolute right-0 top-full border border-[var(--vibe-color-border)] shadow-lg min-w-[240px] z-[70] transition-all duration-200 ${
                moreOpen
                  ? 'opacity-100 visible translate-y-0'
                  : 'opacity-0 invisible -translate-y-1'
              }`}
              style={{ backgroundColor: 'var(--vibe-color-background)' }}
            >
              {overflowLinks.map((link) => (
                <React.Fragment key={navKey(link)}>
                  {link.href ? (
                    <NavAnchor
                      link={link}
                      className="rise-nav-darken-hover block px-5 py-3 font-body text-[13px] font-semibold tracking-wide uppercase text-[var(--vibe-color-text)] transition-colors whitespace-nowrap"
                    />
                  ) : (
                    <span className="block px-5 pt-4 pb-1 font-body text-[11px] font-semibold tracking-[0.15em] uppercase text-[var(--vibe-color-text-muted)] whitespace-nowrap">
                      {link.label}
                    </span>
                  )}
                  {link.children?.map((child) => (
                    <NavAnchor
                      key={child.href}
                      link={child}
                      className="rise-nav-darken-hover block pl-9 pr-5 py-3 font-body text-[13px] font-semibold tracking-wide uppercase text-[var(--vibe-color-text)] transition-colors whitespace-nowrap"
                    />
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

// ============================================================================
// Mobile Nav Link
// ============================================================================

function MobileNavLink({
  link,
  onNavigate,
  isChild = false,
}: {
  link: NavLink;
  onNavigate: () => void;
  isChild?: boolean;
}) {
  return (
    <NavAnchor
      link={link}
      onClick={onNavigate}
      className={`rise-nav-darken-hover block py-3.5 ${isChild ? 'pl-10 pr-6 text-[13px]' : 'px-6 text-sm'} font-body font-semibold uppercase tracking-wider text-[var(--vibe-color-text)] transition-colors border-b border-[var(--vibe-color-border)]/50`}
    />
  );
}

function MobileNavGroup({ link, onNavigate }: { link: NavLink; onNavigate: () => void }) {
  const [open, setOpen] = useState(false);
  const lastPointerType = useRef<string>('');
  return (
    <div
      onPointerEnter={(e) => e.pointerType === 'mouse' && setOpen(true)}
      onPointerLeave={(e) => e.pointerType === 'mouse' && setOpen(false)}
    >
      <button
        type="button"
        onPointerDown={(e) => {
          lastPointerType.current = e.pointerType;
        }}
        onClick={() => {
          if (lastPointerType.current === 'mouse') setOpen(true);
          else setOpen(!open);
          lastPointerType.current = '';
        }}
        aria-expanded={open}
        className="rise-nav-darken-hover w-full flex items-center justify-between py-3.5 px-6 font-body text-sm font-semibold uppercase tracking-wider text-[var(--vibe-color-text)] border-b border-[var(--vibe-color-border)]/50"
      >
        {link.label}
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows,opacity,visibility] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
          open ? 'grid-rows-[1fr] opacity-100 visible' : 'grid-rows-[0fr] opacity-0 invisible'
        }`}
      >
        <div className="overflow-hidden min-h-0">
          {link.href ? <MobileNavLink link={{ label: link.label, href: link.href }} onNavigate={onNavigate} isChild /> : null}
          {link.children!.map((child) => (
            <MobileNavLink key={child.href} link={child} onNavigate={onNavigate} isChild />
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE: Single navigation bar (logo | nav links | utility + CTA).
// The old 3-bar stack (banner + utility bar + nav bar) is replaced by one
// unified bar. Utility links (phone, Apply Now, Resident Access) are
// inlined into the right side of the nav bar on desktop, and folded into
// the mobile drawer on small screens.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);

  const tokens = useTokens();

  // CTA accent color for the white main-nav row. The outline-style
  // `Schedule a Tour` button paints its border / label in this color.
  // When the property's primary is too pale to read against white
  // (contrast < WCAG AA 4.5:1), fall back to the dark `text` token.
  // Hover fill uses secondary independently (see .nav-cta-btn).
  const navCtaAccent = useMemo(() => {
    const primary = tokens?.color?.primary;
    const background = tokens?.color?.background || '#ffffff';
    if (!primary) return 'var(--vibe-color-primary)';
    return contrastRatio(primary, background) >= 4.5
      ? 'var(--vibe-color-primary)'
      : 'var(--vibe-color-text)';
  }, [tokens]);

  // Label color for the solid Self-Guided CTA when it sits on a primary fill
  // (mobile drawer rest state). Uses the same luminance threshold as
  // text-on-primary so medium-tone brand colors (gold/tan) get white text
  // instead of muddy dark-on-medium. Desktop hover fills with secondary and
  // uses text-on-secondary instead.
  const solidCtaOnPrimary = useMemo(() => {
    const primary = tokens?.color?.primary;
    return primary ? getContrastTextColor(primary) : 'var(--vibe-color-text-on-primary)';
  }, [tokens]);

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // FAQs — controls the FAQ page's presence in the nav. A property with no
  // FAQ entries would render an empty FAQ page, so we hide the link entirely
  // when the collection is empty. SitemapContent applies the same filter so
  // the visible sitemap page agrees with the top-nav.
  const faqs = useCollectionBinding<FaqItem>('header.faqs', { collection: 'faqs' });
  const hasFaqs = (faqs || []).some((f) => (f.question && String(f.question).trim()) || (f.answer && String(f.answer).trim()));

  // Interactive Map — drops out of the nav when the property has no
  // ApartmentSync IDs to drive the map on that page (SitemapContent
  // applies the same check).
  const hasApartmentSyncIds = useHasApartmentSyncIds();

  // Editor-configurable override. The `header.interactive_map_show_if_exists`
  // token can be bound to a property field (Field mode — e.g.
  // `custom_engrain_sightmap_url`) or given a literal string (Custom mode).
  // In both cases useContentToken returns the already-RESOLVED value (the
  // field's value, or the literal), so we simply expose the Interactive Map
  // page whenever that resolved value is non-empty. Blank by default (no
  // override). ADDITIVE to the ApartmentSync detection above, so existing
  // ApartmentSync-configured properties keep working untouched.
  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveMapOverride = !!(
    interactiveMapShowIfExists && String(interactiveMapShowIfExists).trim()
  );

  const hasInteractiveLeasing = hasApartmentSyncIds || hasInteractiveMapOverride;

  // Sister Properties — show in More whenever the page is active (visible
  // + hosts the SisterProperties section). Content-filled gating stays on
  // the thank-you CTA; SitemapContent uses the same page-active check.
  const sisterPropertiesActive = isSisterPropertiesPageActive();

  // Floor plan type pages (Studios, 1 Bedroom, …, Townhomes) only appear
  // when the property has floor plans of that type.
  const floorplans = useCollectionBinding<Floorplan>('header.floorplans', { collection: 'floorplans' });

  const marketplaceLink = useSlotGroup('header.marketplace_link', marketplaceLinkDef);

  const navLinks = useMemo(
    () =>
      buildNavLinks(hasVirtualTours, hasInteractiveLeasing, hasFaqs, sisterPropertiesActive, floorplans, {
        url: marketplaceLink.url,
        label: marketplaceLink.label,
        _target: marketplaceLink._target,
      }),
    [
      hasVirtualTours,
      hasInteractiveLeasing,
      hasFaqs,
      sisterPropertiesActive,
      floorplans,
      marketplaceLink.url,
      marketplaceLink.label,
      marketplaceLink._target,
    ],
  );

  // Content tokens
  const phone = useContentToken('header.phone');

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoBlackLogo = usePropertyField('property_mono_black_logo');
  const primaryLogo = usePropertyField('property_primary_logo');

  // Logo color choice: the main nav row is always painted white
  // (--vibe-color-background), and property primary logos are often
  // white/pale. When the slot still shows the property's primary logo we
  // use mono_black unless a pixel sample proves the primary reads on that
  // surface. The media CDN sends no CORS headers, so the sample usually
  // can't run and mono_black wins. Any other logo the editor picks is
  // respected unless the sample proves it invisible.
  const navBackground = tokens?.color?.background || '#ffffff';
  const logoVisible = useLogoVisibleOnBackground(logoRaw.image, navBackground);
  const logoImage = useMemo(() => {
    if (!logoRaw.image) return monoBlackLogo;
    if (logoVisible === true) return logoRaw.image;
    if (logoVisible === false) return monoBlackLogo || logoRaw.image;
    const isPrimaryLogo =
      !!primaryLogo &&
      (parseStorageKey(logoRaw.image) ?? logoRaw.image) === (parseStorageKey(primaryLogo) ?? primaryLogo);
    return isPrimaryLogo ? monoBlackLogo || logoRaw.image : logoRaw.image;
  }, [logoVisible, logoRaw.image, primaryLogo, monoBlackLogo]);
  const logo = { ...logoRaw, image: logoImage };

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const selfGuidedTourLink = useSlotGroup('header.self_guided_tour_link', selfGuidedTourLinkDef);
  const selfGuidedTourUrl = (selfGuidedTourLink.url || '').trim();
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  // Fallback URL when the editor hasn't set a custom address link — hook
  // builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();

  // Google Fonts
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="lexend-deca"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Lexend+Deca:wght@300;400;500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'lexend-deca');
    document.head.appendChild(link);
  }, []);

  // Measure header height for the mobile drawer offset and for page
  // sections (Hero) that need to optically center below the solid bar.
  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      const h = headerRef.current.offsetHeight;
      setHeaderHeight(h);
      document.documentElement.style.setProperty('--header-height', `${h}px`);
    }
  }, []);

  useEffect(() => {
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--header-height');
    };
  }, [updateHeaderHeight]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMobileMenu = () => setMenuOpen(false);

  return (
    <>
      {/* No layout spacer — the solid fixed header overlays the hero image.
          Hero content uses --header-height (set below) to center in the
          visible area under the bar. */}

      <header
        ref={headerRef}
        className="fixed left-0 right-0 z-50 w-full header-scrolled transition-all duration-500 ease-in-out"
        style={{
          top: 0,
          // Luminance-picked CTA accent for the white main-nav row.
          // Stays as primary when the brand color is dark enough,
          // otherwise flips to the theme `text` color so the outline button
          // doesn't disappear into the white header bar. Hover fill uses
          // secondary with text-on-secondary for the label.
          ['--rise-nav-cta-accent' as string]: navCtaAccent,
          ['--rise-nav-solid-cta-on-primary' as string]: solidCtaOnPrimary,
        }}
      >
        {/* Editor-only control. Holds the name of the property field whose
            presence exposes the Interactive Map page in the nav (consumed by
            `hasInteractiveMapOverride` above — e.g. `custom_engrain_sightmap_url`).
            Rendered visually hidden because it's a configuration value, not
            display copy, but wrapped in EditableToken so it's reachable from
            the editor sidebar / inline editor and satisfies the schema linter. */}
        <EditableToken
          path="header.interactive_map_show_if_exists"
          type="text"
          label="Interactive Map: Show If Exists"
          description="Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide."
          as="span"
          className="sr-only"
        >
          {interactiveMapShowIfExists || ''}
        </EditableToken>

        {/* ============================================================== */}
        {/* Single unified navigation bar                                  */}
        {/* ============================================================== */}
        <div
          className="header-bar"
          style={{ paddingTop: 'var(--offer-banner-height, 0px)', transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          {/* ============================================================ */}
          {/* Utility row — desktop only. Sits between the SpecialOfferBanner
              (which adds its height to the header-bar's paddingTop above)
              and the main nav row below. Hosts the phone, Apply Now, and
              Resident Access links that previously lived inline in the main
              nav's right group. Painted with --vibe-color-secondary so it
              reads as a solid brand top bar in both transparent and
              scrolled states; link/divider colors use text-on-secondary.
              The links remain available on mobile via the drawer further
              down — we don't surface a second row on small screens because
              vertical space matters more there. */}
          {/* ============================================================ */}
          <div className="header-util-row hidden lg:block">
            <div className="px-4 sm:px-6 lg:px-8">
              {/* Two-group layout: phone + address sit on the left edge,
                  Apply Now + Resident Access stay on the right. `justify-
                  between` pushes the groups to opposite edges; each group is
                  its own flex row so the inner dividers + gaps behave the
                  same as before. */}
              <div className="flex items-center justify-between gap-1 h-9">
                {/* Left-aligned group: phone + address */}
                <div className="flex items-center gap-1">
                  {/* Phone */}
                  <a
                    href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                    className="dniphone dniphonehref header-util-link"
                    aria-label="Call us"
                  >
                    <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <EditableToken
                      path="header.phone"
                      defaultField="property_call_tracking_phone"
                      defaultValue="(555) 555-5555"
                      as="span"
                    >
                      {formatPhone(phone)}
                    </EditableToken>
                  </a>

                  <span className="header-util-divider" aria-hidden="true" />

                  {/* Address — shares the existing `header.address_link`
                      slot group with the mobile drawer below, so editors
                      see one address+URL pair that drives both surfaces.
                      URL defaults to the property's Google Place URL
                      (opens Maps directly), falls back to '#' if unset. */}
                  <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                    <a
                      href={addressLink.url || propertyAddressUrl || '#'}
                      target={addressLink._target || '_blank'}
                      rel="noopener noreferrer"
                      className="header-util-link"
                      aria-label="View on Google Maps"
                    >
                      <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{addressLink.address}</span>
                    </a>
                  </EditableSlotGroup>
                </div>

                {/* Right-aligned group: utility links */}
                <div className="flex items-center gap-1">
                  {/* Apply Now */}
                  <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                    <a
                      href={applyNowLink.url || '/apply-now'}
                      target={applyNowLink._target || '_self'}
                      className="header-util-link"
                    >
                      {applyNowLink.label || 'Apply Now'}
                    </a>
                  </EditableSlotGroup>

                  <span className="header-util-divider" aria-hidden="true" />

                  {/* Resident Access */}
                  <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                    <a
                      href={residentAccessLink.url || '/resident-access'}
                      target={residentAccessLink._target || '_self'}
                      className="header-util-link"
                    >
                      {residentAccessLink.label || 'Resident Access'}
                    </a>
                  </EditableSlotGroup>
                </div>
              </div>
            </div>
          </div>

          <div className="header-nav-row px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-16 lg:h-[72px]">
              {/* Logo */}
              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                <a
                  href="/"
                  className="flex-shrink-0"
                  data-logo-check={logoVisible === null ? 'unknown' : logoVisible ? 'visible' : 'hidden'}
                >
                  {logo.image ? (
                    <VibesitesImage
                      url={logo.image}
                      alt={logoRaw.alt || 'Community Logo'}
                      className="h-10 lg:h-11 w-auto max-w-[200px] xl:max-w-[220px] object-contain object-left"
                    />
                  ) : (
                    <div className="font-heading text-xl lg:text-2xl font-bold text-[var(--vibe-color-primary)]">
                      The Rise
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Desktop Navigation Links */}
              <DesktopNav navLinks={navLinks} />

              {/* Right group — CTA + hamburger only. Phone / Apply Now /
                  Resident Access moved up to the new utility row above so
                  the main nav stays focused on page links + the primary
                  Schedule-a-Tour CTA. `ml-auto` only below lg: on mobile/
                  tablet DesktopNav is hidden so nothing else fills the
                  row — without it the hamburger sits next to the logo.
                  On lg+, DesktopNav's flex-1 owns the middle gap. */}
              <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0 ml-auto lg:ml-0">
                {/* Tour CTAs — desktop only. On smaller / tablet viewports the
                    same links live in the hamburger drawer (below) so the
                    top bar stays logo + menu without crowding. */}
                <div className="hidden lg:flex items-center gap-2">
                  <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                    <a
                      href={scheduleTourLink.url || '/schedule-a-tour'}
                      target={scheduleTourLink._target || '_self'}
                      className="nav-cta-btn group/cta relative inline-flex items-center gap-2 px-3 py-2.5 lg:px-4 lg:py-3 2xl:px-6 text-[10px] lg:text-[11px] font-medium tracking-[0.15em] uppercase overflow-hidden transition-all duration-300"
                    >
                      <span className="relative z-10">
                        {scheduleTourLink.label || 'Schedule a Tour'}
                      </span>
                    </a>
                  </EditableSlotGroup>

                  {selfGuidedTourUrl ? (
                    <EditableSlotGroup groupPath="header.self_guided_tour_link" definition={selfGuidedTourLinkDef}>
                      <a
                        href={selfGuidedTourUrl}
                        target={selfGuidedTourLink._target || '_blank'}
                        rel={
                          (selfGuidedTourLink._target || '_blank') === '_blank'
                            ? 'noopener noreferrer'
                            : undefined
                        }
                        className="nav-cta-btn nav-cta-btn--solid group/cta relative inline-flex items-center gap-2 px-3 py-2.5 lg:px-4 lg:py-3 2xl:px-5 text-[10px] lg:text-[11px] font-medium tracking-[0.15em] uppercase overflow-hidden transition-all duration-300"
                      >
                        <span className="relative z-10">
                          {selfGuidedTourLink.label || 'Self-Guided Tours'}
                        </span>
                      </a>
                    </EditableSlotGroup>
                  ) : null}
                </div>

                {/* Mobile Hamburger */}
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="lg:hidden relative w-10 h-10 flex items-center justify-center"
                  aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={menuOpen}
                >
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                      menuOpen ? 'rotate-45' : '-translate-y-2'
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                      menuOpen ? 'opacity-0' : ''
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                      menuOpen ? '-rotate-45' : 'translate-y-2'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Mobile Menu Overlay                                            */}
        {/* ============================================================== */}
        <div
          className={`mobile-menu-panel lg:hidden fixed inset-x-0 bottom-0 overflow-y-auto transition-all duration-300 z-[61] ${
            menuOpen
              ? 'opacity-100 visible'
              : 'opacity-0 invisible pointer-events-none'
          }`}
          style={{ top: `${headerHeight}px`, backgroundColor: 'var(--vibe-color-background)' }}
        >
          {/* Navigation links */}
          <nav role="navigation" aria-label="Mobile navigation">
            {navLinks.map((link) =>
              link.children?.length ? (
                <MobileNavGroup key={navKey(link)} link={link} onNavigate={closeMobileMenu} />
              ) : (
                <MobileNavLink key={navKey(link)} link={link} onNavigate={closeMobileMenu} />
              ),
            )}
          </nav>

          {/* Tour CTAs — take the former View Floor Plans slot in the
              drawer. Schedule a Tour is always shown (outline pill);
              Self-Guided Tours (solid fill) only when the custom URL is
              set. Desktop keeps these in the top bar instead. */}
          <div className="p-5 flex flex-col gap-2">
            <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
              <a
                href={scheduleTourLink.url || '/schedule-a-tour'}
                target={scheduleTourLink._target || '_self'}
                onClick={closeMobileMenu}
                className="mobile-util-pill block text-center w-full py-3.5 text-sm font-medium tracking-[0.15em] uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-secondary)] transition-all"
              >
                {scheduleTourLink.label || 'Schedule a Tour'}
              </a>
            </EditableSlotGroup>

            {selfGuidedTourUrl ? (
              <EditableSlotGroup groupPath="header.self_guided_tour_link" definition={selfGuidedTourLinkDef}>
                <a
                  href={selfGuidedTourUrl}
                  target={selfGuidedTourLink._target || '_blank'}
                  rel={
                    (selfGuidedTourLink._target || '_blank') === '_blank'
                      ? 'noopener noreferrer'
                      : undefined
                  }
                  onClick={closeMobileMenu}
                  className="mobile-util-pill mobile-util-pill--solid block text-center w-full py-3.5 text-sm font-medium tracking-[0.15em] uppercase border border-[var(--vibe-color-primary)] bg-[var(--vibe-color-primary)] transition-all"
                >
                  {selfGuidedTourLink.label || 'Self-Guided Tours'}
                </a>
              </EditableSlotGroup>
            ) : null}
          </div>

          {/* Utility CTA Buttons — wrapped in EditableSlotGroup so the
              editor can target them from inside the open mobile drawer
              the same way it can in the desktop nav above. The wrappers
              are display:contents-ish in practice (they don't introduce
              visible chrome), so the two-column grid layout below still
              treats each anchor as a direct grid item. */}
          <div className="px-5 pb-3">
            <div className="grid grid-cols-2 gap-2">
              <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                <a
                  href={applyNowLink.url || '/apply-now'}
                  target={applyNowLink._target || '_self'}
                  className="mobile-util-pill block text-center py-2.5 text-[11px] font-medium tracking-wider uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-secondary)] transition-all"
                >
                  {applyNowLink.label || 'Apply Now'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                <a
                  href={residentAccessLink.url || '/resident-access'}
                  target={residentAccessLink._target || '_self'}
                  className="mobile-util-pill block text-center py-2.5 text-[11px] font-medium tracking-wider uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-secondary)] transition-all"
                >
                  {residentAccessLink.label || 'Resident Access'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Address & Phone */}
          <div className="px-5 pb-6 pt-3 border-t border-[var(--vibe-color-border)]/50">
            <div className="space-y-3">
              <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                <a
                  href={addressLink.url || propertyAddressUrl || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors"
                >
                  <MapPinIcon className="w-4 h-4 text-[var(--vibe-color-primary)] flex-shrink-0" />
                  <span className="rise-nav-darken-hover">{addressLink.address}</span>
                </a>
              </EditableSlotGroup>
              <a
                href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref flex items-center gap-2.5 text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors"
              >
                <PhoneIcon className="w-4 h-4 text-[var(--vibe-color-primary)] flex-shrink-0" />
                {/* Mirror the desktop phone treatment: EditableToken
                    wraps just the inner text span so the editor can
                    target the phone number from inside the open mobile
                    drawer. The outer <a> still routes to `tel:` for
                    real visitors. */}
                <EditableToken
                  path="header.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                  as="span"
                  className="rise-nav-darken-hover"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            </div>
          </div>
        </div>
      </header>

      <style>{`
        /* ================================================================ */
        /* Two-row header: secondary util strip + white main nav           */
        /* ================================================================ */
        .header-bar {
          border-bottom: 1px solid var(--vibe-color-border);
          box-shadow:
            0 1px 3px rgba(0,0,0,0.04),
            0 4px 12px rgba(0,0,0,0.03);
        }

        /* Main nav row — always white so logo / links / CTAs read on a
           solid surface under the primary util strip. */
        .header-nav-row {
          background-color: var(--vibe-color-background, #fff);
        }

        /* "More" dropdown panel sits on a theme-background (white) surface;
           keep link color steady on hover (underline is the affordance). */
        .header-bar .nav-more-dropdown a {
          color: var(--vibe-color-text) !important;
        }
        .header-bar .nav-more-dropdown a:hover {
          color: var(--vibe-color-text) !important;
        }

        /* Reveal dropdowns from CSS too, so hover works before hydration
           and isn't undone by the trigger's click toggle. */
        @media (hover: hover) and (pointer: fine) {
          .header-bar .nav-dropdown-group:hover > .nav-more-dropdown {
            opacity: 1;
            visibility: visible;
            translate: none;
            transform: none;
          }
        }

        /* Mobile menu panel uses --vibe-color-background (white), so links
           must render in theme text color. The hover affordance for nav
           links is the animated underline from .rise-nav-darken-hover. */
        .mobile-menu-panel a,
        .mobile-menu-panel button {
          color: var(--vibe-color-text);
        }
        .mobile-menu-panel a:hover {
          color: var(--vibe-color-text);
        }

        /* Nav links: animated underline on hover. */
        .rise-nav-darken-hover {
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 6px;
          transition: text-decoration-color 0.25s ease, text-underline-offset 0.25s ease, color 0.2s ease;
        }
        .rise-nav-darken-hover:hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
        /* Address / phone rows in the mobile drawer wear the
           rise-nav-darken-hover class on the inner text span (not the link
           itself, so the underline doesn't run under the icon). Trigger
           the underline from the parent link's :hover/:focus-visible so
           the whole row remains the hover target. */
        .mobile-menu-panel a:hover .rise-nav-darken-hover,
        .mobile-menu-panel a:focus-visible .rise-nav-darken-hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
        /* Outline pills inside the drawer flip to text-on-secondary on hover. */
        .mobile-menu-panel .mobile-util-pill:hover {
          color: var(--vibe-color-text-on-secondary);
        }
        /* Solid Self-Guided pill — primary fill with luminance-picked label
           so pale/medium brand colors stay readable. */
        .mobile-menu-panel .mobile-util-pill--solid {
          color: var(--rise-nav-solid-cta-on-primary, var(--vibe-color-text-on-primary));
        }
        .mobile-menu-panel .mobile-util-pill--solid:hover {
          color: var(--rise-nav-solid-cta-on-primary, var(--vibe-color-text-on-primary));
          opacity: 0.92;
        }

        /* ================================================================ */
        /* Utility row — sits above the main nav row inside .header-bar.   */
        /* ----------------------------------------------------------------- */
        /* Always painted with the brand secondary so the top strip reads as
           a solid accent band (phone / address / apply / resident). Link
           and divider colors use text-on-secondary so they stay readable on
           any secondary luminance. Border between util + main nav softens
           slightly so the two rows don't fight. */
        .header-util-row {
          background-color: var(--vibe-color-secondary);
          border-bottom: 1px solid color-mix(in srgb, var(--vibe-color-text-on-secondary) 18%, transparent);
          transition: border-color 0.5s ease, background-color 0.5s ease;
        }

        /* ================================================================ */
        /* Inline utility links (phone, apply, resident)                   */
        /* ================================================================ */
        .header-util-link {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.25rem 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 85%, transparent);
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 5px;
          white-space: nowrap;
          transition: color 0.2s ease, text-decoration-color 0.25s ease, text-underline-offset 0.25s ease;
        }
        .header-util-link:hover {
          color: var(--vibe-color-text-on-secondary);
          text-decoration-color: currentColor;
          text-underline-offset: 3px;
        }
        .header-util-divider {
          display: block;
          width: 1px;
          height: 14px;
          background: color-mix(in srgb, var(--vibe-color-text-on-secondary) 30%, transparent);
          flex-shrink: 0;
        }

        /* ================================================================ */
        /* CTA Button — outline style on white nav row                     */
        /* ----------------------------------------------------------------- */
        /* Border + label use --rise-nav-cta-accent (luminance-picked).
           That variable resolves to --vibe-color-primary when the property's
           primary has WCAG AA contrast against the white nav row, and falls
           back to --vibe-color-text for pale primaries. Hover fills with
           secondary and uses text-on-secondary for the label. */
        .nav-cta-btn {
          background: transparent;
          border: 1.5px solid var(--rise-nav-cta-accent, var(--vibe-color-primary));
          border-radius: 2px;
          color: var(--rise-nav-cta-accent, var(--vibe-color-primary));
          box-shadow: none;
        }
        .nav-cta-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--vibe-color-secondary);
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .nav-cta-btn:hover {
          box-shadow:
            0 2px 6px rgba(0,0,0,0.1),
            0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 25%, transparent);
          transform: translateY(-1px);
          color: var(--vibe-color-text-on-secondary);
          border-color: var(--vibe-color-secondary);
        }
        .nav-cta-btn:hover::before {
          opacity: 1;
        }
        .nav-cta-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }

        /* Solid tour CTA (Self-Guided) — solid background fill + accent
           border/label at rest; fills with secondary on hover. */
        .nav-cta-btn--solid {
          background: var(--vibe-color-background, #fff);
          border: 1.5px solid var(--rise-nav-cta-accent, var(--vibe-color-primary));
          color: var(--rise-nav-cta-accent, var(--vibe-color-primary));
        }
        .nav-cta-btn--solid::before {
          background: var(--vibe-color-secondary);
          opacity: 0;
        }
        .nav-cta-btn--solid:hover {
          color: var(--vibe-color-text-on-secondary);
          border-color: var(--vibe-color-secondary);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 30%, transparent);
        }
        .nav-cta-btn--solid:hover::before {
          opacity: 1;
        }

      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'header.interactive_map_show_if_exists': {
      label: 'Interactive Map: Show If Exists',
      type: 'text',
      description:
        'Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide.',
      defaultValue: '',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/apply-now',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.marketplace_link': {
      label: 'Explore: Marketplace Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: 'https://www.bluhawk.com/' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Bluhawk Marketplace' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/schedule-a-tour',
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.self_guided_tour_link': {
      label: 'Self-Guided Tour Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'custom_self_guided_tour_url',
          suggestedFields: ['custom_self_guided_tour_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Self-Guided Tours' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        // URL has no defaultField — defaults to usePropertyAddressUrl() at
        // render time. Editor overrides still win.
        url: {
          label: 'URL',
          type: 'url',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'header.faqs': {
      label: 'FAQs',
      collection: 'faqs',
    },
    'header.floorplans': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
